import { Link, NavLink } from "react-router-dom";
import Button from "../../../ui/Button";
import style from "./styles/Nabvar.module.scss";
import { useState } from "react";
import paths from "../../../lib/paths";
import { useAppSelector } from "../../../store/hooks";
import useIdleSignOut from "../../../hooks/useIdleSignOut";
import usePersona from "../../../hooks/usePersona";

/**
 * The public site's navigation bar.
 *
 * "Signed in" is whether a session exists in the store, not whether
 * `sessionStorage.userStatus` parses as true: that key was written by the
 * Firebase flow, and `JSON.parse(null)` in the old effect threw on a first
 * visit, which is why the bar sometimes rendered its signed-out state for a
 * signed-in person.
 */
function Navbar() {
    const { handleToggleNavOpen, isNavOpen } = useNavbar();
    const isAuth = useAppSelector((state) => Boolean(state.auth.accessToken));
    const { isApprovedDriver } = usePersona();

    useIdleSignOut();

    return (
        <>
            <nav className={style.navbarContainer}>
                <div className="wrapper-constrain">
                    <div className={style.left_col}>
                        <div className={style.logo}>
                            <Link to="/">
                                <img
                                    src={`${paths.svgs}/hytchBuddyLogo2.svg`}
                                    alt="hytchbuddy logo"
                                />
                            </Link>
                        </div>
                        <ul className={style.links}>
                            <li>
                                <NavLink to="/ride">Ride</NavLink>
                            </li>
                            <li>
                                <NavLink to="/drive">Drive</NavLink>
                            </li>
                            <li>
                                <NavLink to="/safety">Safety</NavLink>
                            </li>
                            <li>
                                <NavLink to="/support">Support</NavLink>
                            </li>
                            <li>
                                <NavLink to="/company">Company</NavLink>
                            </li>
                        </ul>
                    </div>
                    <div className={style.right_col}>
                        <div className={style.lang}>
                            <select title="language">
                                <option value="">ENG</option>
                            </select>
                        </div>
                        {/* Sign Up is the filled button and Log in is plain
                            text, which is the way round the design has them.
                            They were reversed: Log in was the solid blue call
                            to action and Sign Up an outlined button beside it,
                            so the bar pushed returning users at the expense of
                            the one action the marketing site exists to get. */}
                        <div className={style.btn}>
                            {isAuth ? (
                                <Link
                                    to={
                                        isApprovedDriver
                                            ? "/dashboard/driver/overview"
                                            : "/dashboard/rider/overview"
                                    }
                                >
                                    <Button
                                        variant="primary"
                                        size="md"
                                        content={"Dashboard"}
                                    />
                                </Link>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className={style.textLink}
                                    >
                                        Log in
                                    </Link>
                                    <Link to="/sign-up">
                                        <Button
                                            variant="primary"
                                            size="md"
                                            content="Sign Up"
                                        />
                                    </Link>
                                </>
                            )}
                        </div>
                        <div
                            onClick={handleToggleNavOpen}
                            className={style.menuBox}
                        >
                            {isNavOpen ? (
                                <img
                                    src={`${paths.svgs}/closex.svg`}
                                    alt=""
                                />
                            ) : (
                                <img
                                    src={`${paths.svgs}/menu.svg`}
                                    alt=""
                                />
                            )}
                        </div>
                    </div>
                </div>
            </nav>
            {isNavOpen && (
                <div
                    onClick={handleToggleNavOpen}
                    className={style.mobileNavbarOverlay}
                ></div>
            )}
            {/* The open class is appended only when the menu is open. There
                is no closed-state rule: the stylesheet defines
                `.navbar.mobile-nav-component-open` and nothing for the other
                half, so `style["mobile-nav-component"]` was undefined and every
                landing page rendered `class="navbar undefined"` in its closed
                state. Harmless to look at, and exactly the shape of the bug
                that left the Company hero unstyled. */}
            <div
                className={[
                    style.navbar,
                    isNavOpen && style["mobile-nav-component-open"],
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                <ul className={style.mobileLinks}>
                    <li>
                        <NavLink to="/ride">Ride</NavLink>
                    </li>
                    <li>
                        <NavLink to="/drive">Drive</NavLink>
                    </li>
                    <li>
                        <NavLink to="/safety">Safety</NavLink>
                    </li>
                    <li>
                        <NavLink to="/support">Support</NavLink>
                    </li>
                    <li>
                        <Link to="/company">Company</Link>
                    </li>
                    <div className={style.mobileNavButton}>
                        <Link
                            to={isAuth ? "/dashboard/rider/overview" : "/login"}
                        >
                            <Button
                                variant="secondary"
                                size="md"
                                content={isAuth ? "Dashboard" : "Login"}
                            />
                        </Link>

                        <Link to="/sign-up">
                            {/* Hidden rather than not rendered: the Link
                                around it is a flex item, so removing it would
                                close up the gap beside the button that stays. */}
                            <Button
                                variant={isAuth ? "plain" : "secondary"}
                                size={isAuth ? undefined : "md"}
                                classes={isAuth ? "hidden-btn" : "border-0"}
                                content="Sign Up"
                            />
                        </Link>
                    </div>
                </ul>
            </div>
        </>
    );
}

export default Navbar;

const useNavbar = () => {
    const [isNavOpen, setIsNavOpen] = useState(false);

    // The open/closed class is derived in the component rather than returned
    // from here. It used to be a string picked in this hook, which meant the
    // closed branch named a rule that does not exist and nothing could tell.
    const handleToggleNavOpen = () => setIsNavOpen((open) => !open);

    return { handleToggleNavOpen, isNavOpen };
};
